<template>
  <section id="nosotros" class="section-padding bg-background">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-5xl font-bold mb-6">
          Acerca de <span class="text-gradient">Forza TI</span>
        </h2>
        <p class="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
          <strong>Forza TI</strong> es tu socio tecnológico estratégico respaldado por más de 20 años de experiencia 
          de nuestros socios e ingenieros, especializado en impulsar la transformación digital de Empresas Mexicanas. 
          Convertimos los desafíos tecnológicos en oportunidades de crecimiento.
        </p>
      </div>

      <!-- Mission, Vision, Values -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        <div 
          v-for="(value, index) in values"
          :key="index"
          class="card-forza text-center group p-6"
        >
          <div class="pb-4">
            <div class="mx-auto w-16 h-16 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <component :is="value.icon" class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-semibold group-hover:text-primary transition-colors duration-300">
              {{ value.title }}
            </h3>
          </div>
          <div>
            <p class="text-muted-foreground leading-relaxed">
              {{ value.description }}
            </p>
          </div>
        </div>
      </div>

      <!-- Stats Section -->
      <div class="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 border border-primary/20">
        <div class="text-center mb-12">
          <h3 class="text-2xl md:text-3xl font-bold mb-4">
            Números que nos respaldan
          </h3>
          <p class="text-muted-foreground">
            La confianza de nuestros clientes se refleja en estos resultados
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div 
            v-for="(stat, index) in stats"
            :key="index"
            class="text-center group"
          >
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 mb-4 group-hover:bg-primary/30 transition-colors duration-300">
              <component :is="stat.icon" class="w-8 h-8 text-primary" />
            </div>
            <div class="text-4xl md:text-5xl font-bold text-primary mb-2">
              {{ stat.number }}
            </div>
            <div class="text-lg font-semibold mb-2">
              {{ stat.label }}
            </div>
            <div class="text-sm text-muted-foreground">
              {{ stat.description }}
            </div>
          </div>
        </div>
      </div>

      <!-- Why Choose Us -->
      <div class="mt-20 text-center">
        <h3 class="text-2xl md:text-3xl font-bold mb-8">
          ¿Por qué elegir <span class="text-gradient">Forza TI</span>?
        </h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            v-for="(reason, index) in reasons"
            :key="index"
            class="p-6 rounded-lg border border-border hover:border-primary/50 transition-colors duration-300"
          >
            <div class="text-3xl mb-4">{{ reason.emoji }}</div>
            <h4 class="font-semibold mb-2">{{ reason.title }}</h4>
            <p class="text-sm text-muted-foreground">{{ reason.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Target, Eye, Heart, Users, Award, Zap } from 'lucide-vue-next'

const values = [
  {
    icon: Target,
    title: "Misión",
    description: "Conectar estrategia y tecnología para generar resultados reales que impulsen el crecimiento de nuestros clientes."
  },
  {
    icon: Eye,
    title: "Visión",
    description: "Ser la consultora tecnológica de referencia para Empresas Mexicanas, reconocida por nuestra excelencia e innovación."
  },
  {
    icon: Heart,
    title: "Valores",
    description: "Compromiso, Innovación y Transparencia guían cada proyecto que emprendemos junto a nuestros clientes."
  }
]

const stats = [
  {
    icon: Users,
    number: "50+",
    label: "Proyectos completados",
    description: "Implementaciones exitosas realizadas"
  },
  {
    icon: Award,
    number: "20+",
    label: "Años de experiencia",
    description: "De nuestros socios e ingenieros en TI"
  },
  {
    icon: Zap,
    number: "95%",
    label: "Tasa de éxito",
    description: "Proyectos entregados exitosamente"
  }
]

const reasons = [
  {
    emoji: "🎯",
    title: "Enfoque PyME",
    description: "Entendemos las necesidades específicas de las PyMEs mexicanas"
  },
  {
    emoji: "⚡",
    title: "Implementación Ágil",
    description: "Resultados tangibles en tiempos récord"
  },
  {
    emoji: "🛡️",
    title: "Soporte 24/7",
    description: "Acompañamiento continuo post-implementación"
  },
  {
    emoji: "💰",
    title: "ROI Comprobado",
    description: "Inversiones que se reflejan en tu crecimiento"
  }
]
</script>